/**
 * A line diff of `before` against `after`, from their longest common
 * subsequence: `{ kind, text }[]` where kind is "same", "add" or "del", in
 * reading order. `changedOnly` keeps the changed lines and `context` unchanged
 * lines around each, with a "gap" row standing for the lines left out.
 */
export function lineDiff(
  before,
  after,
  { changedOnly = false, context = 2 } = {},
) {
  const a = before.split("\n");
  const b = after.split("\n");
  // lcs[i][j]: the common subsequence length of a[i..] and b[j..].
  const lcs = Array.from(
    { length: a.length + 1 },
    () => new Uint32Array(b.length + 1),
  );
  for (let i = a.length - 1; i >= 0; i--) {
    for (let j = b.length - 1; j >= 0; j--) {
      lcs[i][j] =
        a[i] === b[j]
          ? lcs[i + 1][j + 1] + 1
          : Math.max(lcs[i + 1][j], lcs[i][j + 1]);
    }
  }
  const rows = [];
  let i = 0;
  let j = 0;
  while (i < a.length || j < b.length) {
    if (i < a.length && j < b.length && a[i] === b[j]) {
      rows.push({ kind: "same", text: a[i] });
      i++;
      j++;
    } else if (
      j < b.length &&
      (i === a.length || lcs[i][j + 1] >= lcs[i + 1][j])
    ) {
      rows.push({ kind: "add", text: b[j] });
      j++;
    } else {
      rows.push({ kind: "del", text: a[i] });
      i++;
    }
  }
  if (!changedOnly) return rows;
  const near = rows.map((r, k) =>
    rows
      .slice(Math.max(0, k - context), k + context + 1)
      .some((x) => x.kind !== "same"),
  );
  const out = [];
  rows.forEach((r, k) => {
    if (near[k]) out.push(r);
    else if (out[out.length - 1]?.kind !== "gap")
      out.push({ kind: "gap", text: "" });
  });
  return out;
}
